import { banco } from '../db/banco'
import type { Pedido, SubPedido, ItemPedido } from '../db/tipos'
import { STATUS_VENDA } from '../metas/statusVenda'
import { dentroDoPeriodo } from '../metas/calculo'
import { dataISOLocal } from '../pedidos/precos'
import { unidadesTotais } from './embalagem'
import { mediaDeVendaPorCliente } from './mediaDeVendaPorCliente'

// ▶ A aba Histórico da ficha do produto (T10, 2o teste real dela, 03/09).
//
// Reusa a MESMA regra de "o que conta como venda" que as metas ja usam
// (STATUS_VENDA + pedido.status === 'fechado') -- uma unica definicao, e nao
// uma segunda copia que diverge com o tempo. "Positivado" tambem e a mesma
// ideia de metasDados.ts: clientes distintos entre as vendas do periodo.

export interface HistoricoDoProduto {
  ultimaVenda: { cliente: string; data: string } | null
  /** Unidades vendidas por mes, media dos ultimos 90 dias / 3. */
  mediaVendaMensal: number
  /** % dos clientes positivados (desta representada) que compraram ESTE
   * produto, nos ultimos 30 dias. `null` quando ninguem foi positivado no
   * periodo -- 0/0 nao e "vendeu pra ninguem". */
  mediaVendaPorCliente: number | null
  ultimosPedidos: Array<{
    numero: string
    data: string
    cliente: string
    quantidade: number
    valorUnitario: number
  }>
}

interface Venda {
  pedido: Pedido
  subPedido: SubPedido
  item: ItemPedido
}

const MAX_ULTIMOS_PEDIDOS = 5

async function vendasDaRepresentada(representadaId: string): Promise<Venda[]> {
  const pedidos = await banco.pedidos
    .filter((p) => p.apagado_em === null && p.status === 'fechado')
    .toArray()
  const resultado: Venda[] = []
  for (const pedido of pedidos) {
    const subPedidos = await banco.sub_pedidos
      .where('pedido_id').equals(pedido.id)
      .filter(
        (s) =>
          s.apagado_em === null &&
          s.representada_id === representadaId &&
          STATUS_VENDA.includes(s.status),
      )
      .toArray()
    for (const subPedido of subPedidos) {
      const itens = await banco.itens_pedido
        .where('sub_pedido_id').equals(subPedido.id)
        .filter((i) => i.apagado_em === null)
        .toArray()
      for (const item of itens) resultado.push({ pedido, subPedido, item })
    }
  }
  return resultado
}

async function nomeDoCliente(clienteId: string): Promise<string> {
  const cliente = await banco.clientes.get(clienteId)
  // `?? ''` de proposito: cliente apagado nao pode quebrar a tela do produto.
  return cliente ? (cliente.nome_fantasia ?? cliente.razao_social) : ''
}

export async function historicoDoProduto(
  produtoId: string,
  representadaId: string,
  hoje: Date,
): Promise<HistoricoDoProduto> {
  const todasAsVendas = await vendasDaRepresentada(representadaId)
  const vendasDoProduto = todasAsVendas
    .filter((v) => v.item.produto_id === produtoId)
    .sort((a, b) => (a.pedido.data < b.pedido.data ? 1 : a.pedido.data > b.pedido.data ? -1 : 0))

  const inicio90 = dataISOLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 90))
  const inicio30 = dataISOLocal(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 30))
  const fim = dataISOLocal(hoje)

  const unidadesUltimos90Dias = vendasDoProduto
    .filter((v) => dentroDoPeriodo(v.pedido.data, inicio90, fim))
    .reduce((soma, v) => soma + unidadesTotais(v.item), 0)

  const clientesPositivados30d = new Set(
    todasAsVendas
      .filter((v) => dentroDoPeriodo(v.pedido.data, inicio30, fim))
      .map((v) => v.pedido.cliente_id),
  )
  const clientesDoProduto30d = new Set(
    vendasDoProduto
      .filter((v) => dentroDoPeriodo(v.pedido.data, inicio30, fim))
      .map((v) => v.pedido.cliente_id),
  )

  const maisRecente = vendasDoProduto[0] ?? null

  const ultimosPedidos = await Promise.all(
    vendasDoProduto.slice(0, MAX_ULTIMOS_PEDIDOS).map(async (v) => ({
      numero: v.pedido.numero,
      data: v.pedido.data,
      cliente: await nomeDoCliente(v.pedido.cliente_id),
      quantidade: v.item.quantidade,
      valorUnitario: v.item.preco_unitario,
    })),
  )

  return {
    ultimaVenda: maisRecente
      ? { cliente: await nomeDoCliente(maisRecente.pedido.cliente_id), data: maisRecente.pedido.data }
      : null,
    mediaVendaMensal: Math.round(unidadesUltimos90Dias / 3),
    mediaVendaPorCliente: mediaDeVendaPorCliente(clientesDoProduto30d.size, clientesPositivados30d.size),
    ultimosPedidos,
  }
}
